import React, { useState, useEffect } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import PartnerRegistration from "../components/partner_registration";
import Partnerlist from "../components/partner_list";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchemaRegistPartnerCompanyPerson, validationSchemaEditPartnerCompanyPerson } from '../func/validation_check';
import { fetchData, postData } from "../func/api";
import SkeletonLoading from "../components/skeleton_loading";
import { REPORT_STATUS } from '../const/config';

const MstPartnerList = () => {
    const [companies, setCompanies] = useState([]);
    const [partners, setPartners] = useState([]);
    const [expandedPartner, setExpandedPartner] = useState(false);
    const [editPartnerCompanyPerson, setEditPartnerCompanyPerson] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [partnerToDelete, setPartnerToDelete] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null); // 'register' | 'delete' | 'save'
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control: registControl, handleSubmit: handleRegistSubmit, reset: registReset } = useForm({
        resolver: yupResolver(validationSchemaRegistPartnerCompanyPerson(partners)),
        defaultValues: {
            registPartnerCompanyPerson: {
                companies: "",
                name: "",
                tel: "",
                is_main_person: false
            }
        },
        mode: "onBlur"
    });

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        resolver: yupResolver(validationSchemaEditPartnerCompanyPerson),
        defaultValues: {
            editPartnerCompanyPerson: {
                uuid: "",
                partner_company_name: "",
                person_name: "",
                tel: "",
                is_main_person: false,
            },
        },
        mode: "onBlur",
        context: { partners }
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const [companyList, partnerCompanyPersonList] = await Promise.all([
                    fetchData(`api/get_company_list`),
                    fetchData(`api/get_partner_company_person_list`)
                ]);

                setCompanies(companyList); // 所属会社リスト
                setPartners(partnerCompanyPersonList); // 連絡先担当者一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("データ取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象の連絡先担当者が変わったらフォームをリセット
    useEffect(() => {
        if (editPartnerCompanyPerson) {
            editReset({
                editPartnerCompanyPerson: {
                    uuid: editPartnerCompanyPerson.uuid,
                    partner_company_uuid: editPartnerCompanyPerson.partner_company_uuid,
                    partner_company_name: editPartnerCompanyPerson.partner_company_uuid,
                    person_name: editPartnerCompanyPerson.person_name,
                    tel: editPartnerCompanyPerson.tel,
                    is_main_person: editPartnerCompanyPerson.is_main_person === 1
                }
            });
        }
    }, [editPartnerCompanyPerson, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleCancelEdit = () => {
        setExpandedPartner(false);
        setEditPartnerCompanyPerson(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'register':
                handleRegistSubmit(handleConfirm)(); // 登録フォーム送信
                break;
            case 'delete':
                handleDeleteConfirm(); // 削除処理
                break;
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };


    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    // 「連絡先担当者を登録」ボタンのダイアログ「OK」押下時
    const handleConfirm = async () => {
        try {
            const data = {
                pertner_company_uuid: dialogData.companies,
                name: dialogData.name,
                tel: dialogData.tel,
                is_main_person: dialogData.is_main_person ? 1 : 2
            };
            await postData("api/post_regist_partner_company_person", data);
            setAlertMessage("登録が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
            registReset();
        } catch (err) {
            setAlertMessage("登録に失敗しました");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleEditClick = (partner) => {
        setEditPartnerCompanyPerson({ ...partner });
        setExpandedPartner(partner.uuid);
    };

    const handleDeleteClick = (partner) => {
        // その会社の連絡先担当者数をカウント
        const companyPartnerCount = partners.filter(
            (p) => p.partner_company_uuid === partner.partner_company_uuid
        ).length;

        if (companyPartnerCount === 1) {
            setAlertMessage(`${partner.partner_company_name}の連絡先担当者は最低1つ必要です。`);
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        openDeleteDialog(partner);
        setPartnerToDelete(partner);
        setDeleteDialogOpen(true);
    };

    const openDeleteDialog = (partner) => {
        setDialogType("delete");
        setDialogData(partner); // 削除対象
        setDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setPartners(
            partners.filter((partner) => partner.uuid !== partnerToDelete.uuid)
        );
        setDeleteDialogOpen(false);
        setPartnerToDelete(null);

        // 連絡先担当者論理削除処理
        try {
            const data = {
                uuid: partnerToDelete.uuid
            };

            await postData("api/post_delete_partner_company_person", data);

            setAlertMessage("連絡先担当者の削除が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            // 削除後にデータを再取得
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("削除失敗しました。：", err);
            setAlertMessage("連絡先担当者の削除に失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setPartnerToDelete(null);
            setExpandedPartner(false); // 編集中を閉じる
            setEditPartnerCompanyPerson(null);
        }
    };

    const handleSaveConfirm = async () => {
        try {
            const data = {
                uuid: dialogData.uuid,
                partner_company_uuid: dialogData.partner_company_uuid,
                person_name: dialogData.person_name,
                tel: dialogData.tel,
                is_main_person: dialogData.is_main_person ? 1 : 2
            };

            await postData("api/post_update_partner_company_person", data);

            setPartners(
                partners.map((p) =>
                    p.uuid === dialogData.uuid
                        ? { ...dialogData, is_main_person: dialogData.is_main_person ? 1 : 2 }
                        : p
                )
            );

            setAlertMessage("連絡先担当者の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("連絡先担当者の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedPartner(false);
            setEditPartnerCompanyPerson(null);
            setDialogOpen(false);
        }
    };

    if (loading) {
        return (<SkeletonLoading />);
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                連絡先担当者管理
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 新規連絡先担当者登録 */}
                <PartnerRegistration
                    control={registControl}
                    companies={companies}
                    openDialog={openDialog}
                    handleRegistSubmit={handleRegistSubmit}
                    expanded={true}
                    isDisabled={isDisabled}
                />
                {/* 連絡先担当者一覧 */}
                <Partnerlist
                    control={editControl}
                    partners={partners}
                    expandedPartner={expandedPartner}
                    companies={companies}
                    handleEditClick={handleEditClick}
                    handleCancelEdit={handleCancelEdit}
                    handleDeleteClick={handleDeleteClick}
                    handleEditSubmit={handleEditSubmit}
                    openDialog={openDialog}
                    isDisabled={isDisabled}
                />
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "register" ? "連絡先担当者登録" :
                            dialogType === "delete" ? "連絡先担当者削除" :
                                dialogType === "save" ? "連絡先担当者保存" : ""
                    }
                    message={
                        dialogType === "register" ? "連絡先担当者を登録します。よろしいですか？" :
                            dialogType === "delete" ? `連絡先担当者「${dialogData?.person_name}」を削除しますか？\nこの操作は取り消せません。` :
                                dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "register" ? "OK" :
                            dialogType === "delete" ? "削除" :
                                dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstPartnerList;